import { makeAutoObservable, runInAction } from 'mobx';
import TroubleshootingStore from '../../troubleshooting-page-store';
import { GlobalEEnvironment } from './global-e-credentials-form';

export interface IGlobalEInstallFormData {
    geMerchantGuid: string;
    globalEUserId: string;
    shopUrl: string;
    environment: GlobalEEnvironment;
    currency: string;
    email: string;
    firstname: string;
    surname: string;
}

class GlobalEInstallStore {
    private _request: IGlobalEInstallFormData = {
        geMerchantGuid: '',
        globalEUserId: '',
        shopUrl: '',
        environment: GlobalEEnvironment.PROD,
        currency: 'USD',
        email: '',
        firstname: '',
        surname: '',
    };

    private _isLoading: boolean = false;
    private _showSuccessModal: boolean = false;
    private _successData: { shopId: number; shopUrl: string, messages: string[] } | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    get formData(): IGlobalEInstallFormData {
        return this._request;
    }

    get isLoading(): boolean {
        return this._isLoading;
    }

    get showSuccessModal(): boolean {
        return this._showSuccessModal;
    }

    get successData() {
        return this._successData;
    }

    setGeMerchantGuid(value: string) {
        this._request.geMerchantGuid = value;
    }

    setGlobalEUserId(value: string) {
        this._request.globalEUserId = value;
    }

    setShopUrl(value: string) {
        this._request.shopUrl = value;
    }

    setEnvironment(value: GlobalEEnvironment) {
        this._request.environment = value;
    }

    setCurrency(value: string) {
        this._request.currency = value;
    }

    setEmail(value: string) {
        this._request.email = value;
    }

    setFirstname(value: string) {
        this._request.firstname = value;
    }

    setSurname(value: string) {
        this._request.surname = value;
    }

    setShowSuccessModal(value: boolean) {
        this._showSuccessModal = value;
    }

    async createStore(): Promise<void> {
        runInAction(() => {
            this._isLoading = true;
            this._showSuccessModal = false;
            this._successData = null;
        });

        try {
            const response = await TroubleshootingStore.createPlatformStore({
                platform: 'global_e',
                credentials: {
                    ge_merchant_guid: this._request.geMerchantGuid.trim(),
                    global_e_user_id: this._request.globalEUserId.trim(),
                    shop_url: this._request.shopUrl.trim(),
                    environment: this._request.environment,
                    currency: this._request.currency.trim(),
                },
                email: this._request.email.trim(),
                firstname: this._request.firstname.trim(),
                surname: this._request.surname.trim(),
            });

            const shopData = response?.data || response;
            const shopId = shopData?.shop_user_id;
            const shopName = shopData?.shop_name || '';

            if (shopId) {
                runInAction(() => {
                    this._successData = { shopId, shopUrl: shopName, messages: shopData.messages };
                    this._showSuccessModal = true;
                });

                this.resetForm();
            }
        } finally {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    resetForm(): void {
        runInAction(() => {
            this._request = {
                geMerchantGuid: '',
                globalEUserId: '',
                shopUrl: '',
                environment: GlobalEEnvironment.PROD,
                currency: 'USD',
                email: '',
                firstname: '',
                surname: '',
            };
        });
    }
}

export default GlobalEInstallStore;

